import { Box, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { winState } from "../../../redux/win/winSlice";
import { ignorePrice, loadEventWin } from "../../../redux/win/winActions";
import { dispatchIsSuccessful, getEventDesc } from "../../../utils/commonUtils";
import { useAlerts, useFeedback } from "@sis/react-library";
import { useState } from "react";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { rdsEvent_Dto } from "../../../interfaces/dtos/rdsEvent";
import { Win } from "../../../interfaces/dtos/win";
import { ScheduleActions } from "../schedulePage/ScheduleActions";
import { ScheduleCommonRefresh } from "../scheduleCommon/ScheduleCommonRefresh";
import { autoRefreshTopics } from "../../shared/commonConstants";
import BlockIcon from '@mui/icons-material/Block';
import { providers } from "../../../variables";
import { getDisabledCriteria } from "../../shared/disabledCriteria";
import { Track } from "../../../interfaces/dtos/track";

interface ScheduleWinActionsRightProps {
  eventId?: number;
  event?: rdsEvent_Dto;
  currentEventWin?: Win;
  track?: Track;
}

export const ScheduleWinActionsRight = ({
  eventId,
  event,
  currentEventWin,
  track
}: ScheduleWinActionsRightProps) => {

  const win = useAppSelector(winState);
  const dispatch = useAppDispatch();
  const { confirm } = useAlerts();
  const { showSuccess } = useFeedback();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleRefresh = () => {
    if (eventId) {
      dispatch(loadEventWin(eventId, true));
    }
  };

  const callIgnorePrices = async () => {
    if (currentEventWin) {
      const response = await dispatch(
        ignorePrice({
          eventId: currentEventWin.eventId,
          currentVersion: currentEventWin.version
        })
      );
      if (dispatchIsSuccessful(response)) {
        showSuccess(`Successfully ignored prices.`);
      }
    }
  };

  const handleIgnore = () => {
    setAnchorEl(null);
    const confirmMessage = (
      <>
        {`You are attempting to move the Win Price Market for ${getEventDesc(
          event
        )} to Ignore.`}
        <span style={{ color: "red" }}>
          The feed for this event can't be turned on again once this action is
          completed.
        </span>
        <br />
        <br />
        Do you wish to proceed?
      </>
    );
    confirm(
      "Ignore these prices",
      confirmMessage,
      async () => {
        callIgnorePrices();
      },
      "Yes",
      "No"
    );
  };

  // Only show ignore if not ignored already, and the provider is not PA.
  const ignoreDisabledResult = getDisabledCriteria([
    {
      condition: !track,
      reason: "the track could not be loaded"
    },
    {
      condition: currentEventWin?.provider === providers.pa,
      reason: "you can't ignore for provider PA"
    }
  ], true);

  return (
    <Box sx={{ margin: "-19px -10px 0 0", display: "flex", alignItems: "center" }}>
      {currentEventWin && !currentEventWin.calculated?.isIgnorePrice &&
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton
            aria-label="more"
            id="menu-button"
            aria-controls={open ? "win-menu" : undefined}
            aria-expanded={open ? "true" : undefined}
            aria-haspopup="true"
            onClick={handleOpenMenu}
            sx={{ color: "#fff", marginTop: '4px' }}
          >
            <MoreVertIcon />
          </IconButton>
          <Menu
            id="win-menu"
            anchorEl={anchorEl}
            sx={{ mt: '6px' }}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            open={open}
            onClose={handleCloseMenu}
          >
            <Tooltip title={ignoreDisabledResult.reason}>
              <span>
                <MenuItem
                  id="win-ignore"
                  disabled={ignoreDisabledResult.isDisabled}
                  onClick={handleIgnore}
                >
                  <ListItemIcon>
                    <BlockIcon fontSize="small" color="warning" />
                  </ListItemIcon>
                  <ListItemText>Ignore these prices</ListItemText>
                </MenuItem>
              </span>
            </Tooltip>
          </Menu>
        </Box>
      }
      <ScheduleActions />
      {eventId && (
        <ScheduleCommonRefresh
          eventId={eventId}
          topic={autoRefreshTopics.winMarket}
          desc="win"
          loading={win.reloading}
          onRefresh={handleRefresh}
        />
      )}
    </Box>
  );
};
